import React, { useEffect, useCallback, useRef, memo } from 'react';
import { useSelector, useDispatch, connect } from 'react-redux';
import Helmet from 'react-helmet';

import { MailingLogs } from '../components/MailingLogs';
import { buildSortByParam } from '../helpers/paramBuilder';
import { getMailingLogs, updateMailingLogs } from '../actions/mailingLogs';
import { getMailingHeaderInfo } from '../actions/mailingInfo';
import { showError } from '../utils/MessagePopup';
import config from '../config';
import { isResearcher } from '../utils/checkPermissions';
import { usePopupContext } from '../contexts/PopupContext';

const MailingLogsContainerComponent = ({ history, match }) => {
  const dispatch = useDispatch();
  const mailingLogs = useSelector(state => state.mailingLogs);
  const mailingInfo = useSelector(state => state.mailingInfo);
  const user = useSelector(state => state.auth.get('user', null));
  const batchId = match.params.id;

  const { openPopup, closePopup } = usePopupContext();

  const hasAccess = useCallback(() => {
    if (!user) return false;

    return !isResearcher(user.getIn(['roles', 0, 'slug'], null));
  }, [user]);

  useEffect(() => {
    if (!hasAccess()) {
      showError(openPopup, config.permisionError, () => {
        history.push('/');
        closePopup();
      });

      return;
    }

    if (user && batchId) {
      dispatch(getMailingLogs({ batchId }));
    }

    if (batchId) {
      dispatch(getMailingHeaderInfo({ batchId }));
    }
  }, [user, batchId, hasAccess, history, openPopup, closePopup]);

  const prevSortByRef = useRef(buildSortByParam(mailingLogs));

  useEffect(() => {
    const currentSortBy = buildSortByParam(mailingLogs);

    if (currentSortBy !== prevSortByRef.current && user) {
      prevSortByRef.current = currentSortBy;
      dispatch(
        getMailingLogs({
          batchId,
          page: 1,
          sortBy: currentSortBy,
        }),
      );
    }
  }, [dispatch, mailingLogs, user]);

  const handleGetNextPageData = useCallback(
    page => {
      if (!user) return;

      const sortBy = buildSortByParam(mailingLogs);

      dispatch(
        getMailingLogs({
          batchId,
          page,
          sortBy,
        }),
      );
    },
    [dispatch, mailingLogs, user],
  );

  const handleChange = useCallback(
    data => {
      dispatch(
        updateMailingLogs({
          filterName: data.filterName,
          filterData: data.filterData,
        }),
      );
    },
    [dispatch],
  );

  const handleReload = useCallback(() => {
    if (!user) return;
    dispatch(
      getMailingLogs({
        batchId,
        page: 1,
      }),
    );
  }, [dispatch, user]);

  if (!hasAccess()) return null;

  return (
    <div className="full-height">
      <Helmet title="Mailing Logs Page" />
      <MailingLogs
        mailingInfo={mailingInfo}
        mailingLogs={mailingLogs}
        onChange={handleChange}
        onGetNextPageData={handleGetNextPageData}
        onReload={handleReload}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  emailTemplates: state.emailTemplatesSendGrid.get('emailTemplates'),
  isLoading: state.emailTemplatesSendGrid.get('loading'),
  sort: state.emailTemplatesSendGrid.get('sort'),
});

export const MailingLogsContainer = memo(connect(mapStateToProps, null)(MailingLogsContainerComponent));
